import React, { useMemo } from 'react';

import { runningInRNWebView, useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';

import { PendingCardTokenItem } from '@/domain/digital-wallet';
import piwik from '@/piwik/piwik';

import ApplePayPendingCardTokenActivationButton from '@domain/digital-wallet/components/apple-pay/ApplePayPendingCardTokenActivationButton';
import { useActivateDigitalWalletPendingCard } from '@domain/digital-wallet/hooks/use-digital-wallet-pending-cards';
import { DeviceType } from '@generated-types';
import { useCardId } from '@hooks/card/use-card-id';
import useText from '@hooks/use-text';

type Props = {
    pendingCardTokens: PendingCardTokenItem[];
};

const getItemDeviceType = (item: PendingCardTokenItem) =>
    item.source === 'te' ? item.token.deviceType : (item.token.device.type as DeviceType);

const ApplePayPendingCardToken: React.FC<Props> = ({ pendingCardTokens }) => {
    const text = useText();
    const cardId = useCardId();

    const nativeProps = useReactNativeProps();

    const enabled = useMemo(() => Boolean(nativeProps.supports?.applePay) && Boolean(cardId), [nativeProps, cardId]);

    const { activateCard, statusArray, hasLoading } = useActivateDigitalWalletPendingCard(enabled);

    if (!enabled || !runningInRNWebView || pendingCardTokens.length === 0) return null;

    return (
        <section className="pending-card-detail-overview-wrapper" data-testid="pending-card-detail-wrapper">
            <ApplePayPendingCardTokenActivationButton
                id="phone"
                activateCardToken={activateCard}
                pendingCardTokens={pendingCardTokens.filter((token) =>
                    [DeviceType.MOBILEPHONE_OR_TABLET, DeviceType.MOBILE_PHONE].includes(getItemDeviceType(token)),
                )}
                statusArray={statusArray}
                hasLoading={hasLoading}
                label={text.completeVerificationIPhone}
                data-track-id={piwik.applePay.applePayActivateIphone.dataTrackId}
                data-track-linkname={piwik.applePay.applePayActivateIphone.dataTrackLinkname}
            />
            <ApplePayPendingCardTokenActivationButton
                id="tablet"
                activateCardToken={activateCard}
                pendingCardTokens={pendingCardTokens.filter((token) =>
                    [DeviceType.TABLET].includes(getItemDeviceType(token)),
                )}
                statusArray={statusArray}
                hasLoading={hasLoading}
                label={text.completeVerificationIPad}
                data-track-id={piwik.applePay.applePayActivateIpad.dataTrackId}
                data-track-linkname={piwik.applePay.applePayActivateIpad.dataTrackLinkname}
            />
            <ApplePayPendingCardTokenActivationButton
                id="watch"
                activateCardToken={activateCard}
                pendingCardTokens={pendingCardTokens.filter((token) =>
                    [DeviceType.WATCH].includes(getItemDeviceType(token)),
                )}
                statusArray={statusArray}
                hasLoading={hasLoading}
                label={text.completeVerificationWatch}
                data-track-id={piwik.applePay.applePayActivateWatch.dataTrackId}
                data-track-linkname={piwik.applePay.applePayActivateWatch.dataTrackLinkname}
            />
        </section>
    );
};

export default ApplePayPendingCardToken;
